import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, ArrowUpRight } from "lucide-react";
import { useLang } from "./LanguageContext";
import { waLink, LOGO_URL } from "./content";

export const ChatWidget = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.92 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="w-[300px] sm:w-[340px] overflow-hidden border border-white/10 bg-[#0a0a0a] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]"
          >
            {/* header */}
            <div className="relative bg-[#ccff00] text-black p-5">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="NEXCIP" className="h-10 w-10 object-cover rounded-sm border border-black/10" />
                <div className="leading-tight">
                  <span className="font-display font-bold text-base">{t.chat.title}</span>
                  <span className="flex items-center gap-1.5 text-xs font-medium mt-0.5">
                    <span className="h-2 w-2 rounded-full bg-green-600 animate-pulse" />
                    {t.chat.subtitle}
                  </span>
                </div>
              </div>
            </div>

            {/* quick replies */}
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {t.chat.quick.map((q, i) => (
                  <a
                    key={i}
                    href={waLink(q.msg)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`chat-quick-${i}`}
                    className="inline-flex items-center px-3 py-2 border border-white/15 bg-white/[0.03] text-xs font-medium text-zinc-200 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                  >
                    {q.label}
                  </a>
                ))}
              </div>
              <a
                href={waLink(t.chat.directMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chat-direct"
                className="group mt-4 flex items-center justify-between w-full h-12 px-4 bg-[#ccff00] text-black font-bold text-sm hover:bg-[#b3e600] transition-colors"
              >
                <span className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  {t.chat.direct}
                </span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="whatsapp-float"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Chat"
        className="flex items-center gap-3 h-14 pl-4 pr-5 bg-[#ccff00] text-black font-bold text-sm shadow-[0_0_40px_-8px_rgba(204,255,0,0.6)]"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
        <span className="hidden sm:inline">{open ? "Close" : "WhatsApp"}</span>
      </motion.button>
    </div>
  );
};
